import { FileArchive, FileText } from "lucide-react";
import { StatusDot } from "@shared/components/blocks";
import type { MarketingFileView, MarketingItemView } from "@shared/lib/marketing-shared";
import { cn } from "@shared/lib/utils";
import { AssetHeading } from "./asset-row";
import { DownloadAction, ViewAction } from "./file-actions";

function LogoCell({ file }: { file: MarketingFileView }) {
  const facts = [file.format, file.dimensions, file.size].filter(Boolean).join(" · ");
  return (
    <div className="flex min-w-0 flex-col border-r border-b">
      {/* Les versions blanches évidées sur fond sombre, les autres sur blanc. */}
      <div className={cn("flex h-28 items-center justify-center px-4", file.background === "dark" ? "bg-foreground" : "bg-background")}>
        {file.missing ? (
          <StatusDot tone="danger">Fichier absent</StatusDot>
        ) : file.kind === "image" ? (
          <img alt={file.label} className="h-11 w-full object-contain" loading="lazy" src={file.url} />
        ) : (
          // Kit (ZIP), mode d'emploi (PDF) : pas d'aperçu, le format suffit.
          <span className="flex flex-col items-center gap-1.5 text-[12px] text-muted-foreground">
            {file.kind === "pdf" ? <FileText aria-hidden="true" className="size-5" /> : <FileArchive aria-hidden="true" className="size-5" />}
            {file.format}
          </span>
        )}
      </div>
      {/* Sous sm, la tuile fait la moitié de l'écran : les actions passent sous le texte, alignées en bas. */}
      <div className="flex flex-1 items-start justify-between gap-2 border-t py-2.5 pr-1.5 pl-3.5 max-sm:flex-col max-sm:gap-1 max-sm:pr-3 max-sm:pb-1">
        <div className="min-w-0 pt-0.5">
          <p className="m-0 text-[13px] leading-snug font-medium">{file.label}</p>
          <p className="m-0 text-[12px] text-muted-foreground tabular-nums">{facts}</p>
          {file.usage && <p className="m-0 mt-0.5 text-[12px] leading-snug text-muted-foreground">{file.usage}</p>}
        </div>
        <div className="flex shrink-0 max-sm:mt-auto max-sm:-ml-3.5">
          {/* Un PDF s'ouvre dans un nouvel onglet ; une image se voit déjà dans la cellule. */}
          {file.kind === "pdf" && <ViewAction file={file} onPreview={() => {}} />}
          <DownloadAction file={file} />
        </div>
      </div>
    </div>
  );
}

/** Logos : chaque déclinaison sur le fond qui lui est destiné, dans une grille à filets. */
export function LogoGroup({ item }: { item: MarketingItemView }) {
  return (
    <article>
      <div className="px-4 py-4 max-sm:px-3.5">
        <AssetHeading item={item} />
      </div>
      <div className="overflow-hidden border-t">
        <div className="-mr-px -mb-px grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {item.variants.map((file) => (
            <LogoCell file={file} key={file.key} />
          ))}
        </div>
      </div>
    </article>
  );
}
